import { useState, useEffect, useRef } from 'react';
import { GA_COUNTIES } from '../lib/gaCounties';
import { countyForZip } from '../lib/zipCounty';
import { submitIntakeForm, getProfiles } from '../lib/supabase';
import { listPublicPhysicians } from '../lib/directory';
import PhysicianPicker from '../components/directory/PhysicianPicker';
import { sanitise } from '../lib/audit';
import { notifyNewIntake } from '../lib/notifications';
import PetsQuestion from '../components/PetsQuestion';
import { PET_COPY, petAnswerError, petColumns } from '../lib/pets';

const TRANSLATIONS = {
  en: {
    title: 'Client Intake Form',
    subtitle: 'A Heart For Care — Home Nursing Services',
    selectClientType: 'What do you need?',
    clientTypeDesc: 'Select everything the client needs. You can choose both.',
    selectPayer: 'How will this be paid?',
    payerDesc: 'If you are not sure, choose the closest option — a coordinator will confirm.',
    gappClient: 'GAPP',
    gappDesc: 'Georgia Pediatric Program — Medicaid-funded nursing & personal care for children under 21',
    medicaidClient: 'Medicaid',
    medicaidDesc: 'Regular Medicaid, not through GAPP',
    insuranceClient: 'Private Insurance / CMO',
    insuranceDesc: 'A commercial health plan, or a Medicaid CMO such as Amerigroup, CareSource or Peach State',
    selfPayClient: 'Self-Pay',
    selfPayDesc: 'You will pay for services directly',
    clientInfo: 'Client Information',
    patientInfo: 'Patient Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    dob: 'Date of Birth',
    gender: 'Gender',
    selectGender: 'Select gender',
    language: 'Primary Language',
    address: 'Home Address',
    city: 'City',
    county: 'County',
    state: 'State',
    zip: 'ZIP Code',
    medicaidId: 'Medicaid ID Number',
    privateInsurance: 'Does the client have private insurance?',
    yes: 'Yes', no: 'No',
    insuranceName: 'Insurance Name & Member ID',
    guardianInfo: 'Parent / Guardian Information',
    responsible: 'Responsible Party Information',
    guardianName: 'Parent/Guardian Full Name',
    responsibleName: 'Responsible Party Full Name',
    guardianRelationship: 'Relationship',
    guardianPhone: 'Phone Number',
    guardianEmail: 'Email Address',
    emergencyInfo: 'Emergency Contact',
    emergencyRelationship: 'Relationship',
    emergencyPhone: 'Phone Number',
    contactAddressLabel: 'Address',
    sameAsGuardianLabel: 'Same as Parent / Guardian above',
    sameAsResponsibleLabel: 'Same as Responsible Party above',
    sameAsCopySub: '— copies name, relationship, phone, email & address',
    quickFillChildAddress: "↳ Same as child's address",
    quickFillAboveAddress: '↳ Same as above',
    medicalInfo: 'Medical Information',
    diagnosisSummary: 'Diagnoses / Conditions',
    doctorSearch: 'Primary Care Doctor',
    doctorSearchHint: 'Start typing the doctor\'s name or practice and pick them from the list.',
    doctorSearchPlaceholder: 'Doctor\'s name or practice…',
    doctorNotListed: (q) => <>Can't find them? <b>Enter the doctor's details below</b></>,
    doctorNoMatch: (q) => `No doctor named “${q}” in our list yet — enter their details below.`,
    doctorTyped: 'Doctor not in our list — tell us what you know',
    doctorName: 'Primary Care Doctor\'s Name',
    doctorPractice: 'Doctor\'s Practice Name',
    doctorAddress: 'Doctor\'s Office Address',
    doctorPhone: 'Doctor\'s Office Phone',
    referralSource: 'How did you hear about us?',
    selectSource: 'Select referral source',
    sources: ['CHOA', 'KIDMED', 'Doctor referral', 'Word of mouth', 'Online search', 'Social media', 'Other'],
    consentLabel: 'I consent to receive text messages and emails from A Heart For Care regarding care.',
    signatureLabel: 'Electronic Signature (type your full name)',
    signaturePlaceholder: 'Type your full legal name',
    submit: 'Submit Intake Form',
    submitting: 'Submitting...',
    required: 'Required',
    successTitle: 'Intake Form Submitted!',
    successMsg: 'Thank you! Your intake form has been received. A member of our team will contact you within 1-2 business days.',
    successSub: 'If you have any urgent questions, please call us directly.',
    errorRequired: 'Please fill in all required fields and select a service type.',
    errorEmail: 'Please enter a valid email address.',
    errorGeneral: 'Something went wrong. Please try again.',
    male: 'Male', female: 'Female', other: 'Other',
    selectCounty: '— Select County —',
    countyFromZip: "Filled in from your ZIP code. Change it if it isn't right.",
    countyChoose: 'Your ZIP code covers {list}. Choose your county.',
    servicesRequested: 'Services Requested',
    servicesRequestedDesc: 'Select all services this family is requesting:',
    homeHealthLabel: '🏡 Home Health Nursing',
    homeHealthDesc: 'Skilled nursing care at home',
    dmeLabel: '📦 Equipment & Supplies (DME)',
    dmeDesc: 'Incontinence, ostomy, catheter supplies',
    dmeDetailsTitle: 'Equipment & Supplies Details',
    dmeDetailsDesc: 'Tell us about the supplies needed. A coordinator will confirm product codes and arrange authorisation.',
    productCategoriesLabel: 'Product categories needed',
    incontinenceLabel: 'Incontinence supplies',
    ostomyLabel: 'Ostomy supplies',
    catheterLabel: 'Catheters',
    qualifyingDiagnosisLabel: 'Qualifying diagnosis (reason supplies are needed)',
    selectPlaceholder: '— Select —',
    dmeDiagnoses: ['Cerebral palsy', 'Spina bifida', 'Autism spectrum disorder', 'Neurogenic bladder', 'Spinal cord injury', 'Intellectual disability', "Crohn's disease", 'Ulcerative colitis', 'Bowel/bladder dysfunction', 'Other'],
    prescribingPhysicianLabel: 'Prescribing physician name',
    physicianNpiLabel: 'Physician NPI number',
    deliveryAddressLabel: 'Delivery address',
    sameAsHomeAddress: 'Same as home address above',
    deliveryAddressPlaceholder: 'Delivery address',
  },
  es: {
    title: 'Formulario de Admisión de Clientes',
    subtitle: 'A Heart For Care — Servicios de Enfermería en el Hogar',
    selectClientType: '¿Qué necesita?',
    clientTypeDesc: 'Seleccione todo lo que el cliente necesita. Puede elegir ambos.',
    selectPayer: '¿Cómo se pagará?',
    payerDesc: 'Si no está seguro, elija la opción más cercana — un coordinador lo confirmará.',
    gappClient: 'GAPP',
    gappDesc: 'Programa Pediátrico de Georgia — enfermería y cuidado personal financiado por Medicaid para menores de 21 años',
    medicaidClient: 'Medicaid',
    medicaidDesc: 'Medicaid regular, no a través de GAPP',
    insuranceClient: 'Seguro Privado / CMO',
    insuranceDesc: 'Un plan de salud comercial, o un CMO de Medicaid como Amerigroup, CareSource o Peach State',
    selfPayClient: 'Pago Directo',
    selfPayDesc: 'Usted pagará los servicios directamente',
    clientInfo: 'Información del Cliente',
    patientInfo: 'Información del Paciente',
    firstName: 'Nombre',
    lastName: 'Apellido',
    dob: 'Fecha de Nacimiento',
    gender: 'Género',
    selectGender: 'Seleccionar género',
    language: 'Idioma Principal',
    address: 'Dirección del Hogar',
    city: 'Ciudad',
    county: 'Condado',
    state: 'Estado',
    zip: 'Código Postal',
    medicaidId: 'Número de Medicaid',
    privateInsurance: '¿El cliente tiene seguro privado?',
    yes: 'Sí', no: 'No',
    insuranceName: 'Nombre del Seguro y Número de Miembro',
    guardianInfo: 'Información del Padre / Tutor',
    responsible: 'Información de la Parte Responsable',
    guardianName: 'Nombre Completo del Padre/Tutor',
    responsibleName: 'Nombre Completo de la Parte Responsable',
    guardianRelationship: 'Relación',
    guardianPhone: 'Número de Teléfono',
    guardianEmail: 'Correo Electrónico',
    emergencyInfo: 'Contacto de Emergencia',
    emergencyRelationship: 'Relación',
    emergencyPhone: 'Número de Teléfono',
    contactAddressLabel: 'Dirección',
    sameAsGuardianLabel: 'Igual que el Padre / Tutor indicado arriba',
    sameAsResponsibleLabel: 'Igual que la Parte Responsable indicada arriba',
    sameAsCopySub: '— copia nombre, relación, teléfono, correo y dirección',
    quickFillChildAddress: '↳ Igual que la dirección del niño',
    quickFillAboveAddress: '↳ Igual que arriba',
    medicalInfo: 'Información Médica',
    diagnosisSummary: 'Diagnósticos / Condiciones',
    doctorSearch: 'Médico de Cabecera',
    doctorSearchHint: 'Escriba el nombre del médico o del consultorio y elíjalo de la lista.',
    doctorSearchPlaceholder: 'Nombre del médico o del consultorio…',
    doctorNotListed: (q) => <>¿No lo encuentra? <b>Escriba los datos del médico abajo</b></>,
    doctorNoMatch: (q) => `Todavía no tenemos un médico llamado “${q}” — escriba sus datos abajo.`,
    doctorTyped: 'Médico que no está en nuestra lista — díganos lo que sepa',
    doctorName: 'Nombre del Médico de Cabecera',
    doctorPractice: 'Nombre del Consultorio',
    doctorAddress: 'Dirección del Consultorio',
    doctorPhone: 'Teléfono del Consultorio',
    referralSource: '¿Cómo se enteró de nosotros?',
    selectSource: 'Seleccionar fuente',
    sources: ['CHOA', 'KIDMED', 'Referencia médica', 'Recomendación personal', 'Búsqueda en línea', 'Redes sociales', 'Otro'],
    consentLabel: 'Doy mi consentimiento para recibir mensajes de texto y correos electrónicos de A Heart For Care sobre el cuidado.',
    signatureLabel: 'Firma Electrónica (escriba su nombre completo)',
    signaturePlaceholder: 'Escriba su nombre legal completo',
    submit: 'Enviar Formulario de Admisión',
    submitting: 'Enviando...',
    required: 'Requerido',
    successTitle: '¡Formulario Enviado!',
    successMsg: '¡Gracias! Hemos recibido su formulario de admisión. Un miembro de nuestro equipo se comunicará con usted dentro de 1-2 días hábiles.',
    successSub: 'Si tiene alguna pregunta urgente, por favor llámenos directamente.',
    errorRequired: 'Por favor complete todos los campos obligatorios y seleccione un tipo de cliente.',
    errorEmail: 'Por favor ingrese una dirección de correo electrónico válida.',
    errorGeneral: 'Algo salió mal. Por favor intente de nuevo.',
    male: 'Masculino', female: 'Femenino', other: 'Otro',
    selectCounty: '— Seleccionar Condado —',
    countyFromZip: 'Completado según su código postal. Cámbielo si no es correcto.',
    countyChoose: 'Su código postal abarca {list}. Elija su condado.',
    servicesRequested: 'Servicios Solicitados',
    servicesRequestedDesc: 'Seleccione todos los servicios que esta familia está solicitando:',
    homeHealthLabel: '🏡 Enfermería en el Hogar',
    homeHealthDesc: 'Cuidado de enfermería especializada en el hogar',
    dmeLabel: '📦 Equipo y Suministros (DME)',
    dmeDesc: 'Suministros de incontinencia, ostomía y catéteres',
    dmeDetailsTitle: 'Detalles de Equipo y Suministros',
    dmeDetailsDesc: 'Cuéntenos sobre los suministros necesarios. Un coordinador confirmará los códigos de producto y gestionará la autorización.',
    productCategoriesLabel: 'Categorías de productos necesarias',
    incontinenceLabel: 'Suministros de incontinencia',
    ostomyLabel: 'Suministros de ostomía',
    catheterLabel: 'Catéteres',
    qualifyingDiagnosisLabel: 'Diagnóstico calificante (motivo por el cual se necesitan los suministros)',
    selectPlaceholder: '— Seleccionar —',
    dmeDiagnoses: ['Parálisis cerebral', 'Espina bífida', 'Trastorno del espectro autista', 'Vejiga neurogénica', 'Lesión de la médula espinal', 'Discapacidad intelectual', 'Enfermedad de Crohn', 'Colitis ulcerosa', 'Disfunción intestinal/vesical', 'Otro'],
    prescribingPhysicianLabel: 'Nombre del médico que prescribe',
    physicianNpiLabel: 'Número NPI del médico',
    deliveryAddressLabel: 'Dirección de entrega',
    sameAsHomeAddress: 'Igual a la dirección del hogar indicada arriba',
    deliveryAddressPlaceholder: 'Dirección de entrega',
  }
};

const Field = ({ label, required, children }) => (
  <div className="form-group">
    <label className="form-label">{label}{required && <span className="req"> *</span>}</label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14, paddingBottom: 8, borderBottom: '2px solid var(--border)' }}>{title}</div>
    {children}
  </div>
);

export default function IntakeForm() {
  const [lang, setLang] = useState('en');
  const t = TRANSLATIONS[lang];

  // The public directory list (names and practices only). Picking a doctor
  // links the submission; typing one queues it for a coordinator to match.
  const [publicPhysicians, setPublicPhysicians] = useState([]);
  const [pickedDoctor, setPickedDoctor] = useState(null);
  const [typeDoctor, setTypeDoctor] = useState(false);
  useEffect(() => { listPublicPhysicians().then(({ data }) => setPublicPhysicians(data || [])); }, []);
  const pickDoctor = (p) => {
    setPickedDoctor(p);
    setTypeDoctor(false);
    setForm(f => ({ ...f, physician_id: p ? p.id : null,
      doctor_name: p ? `${p.first_name} ${p.last_name}`.trim() : '', doctor_practice: p ? (p.facility_name || '') : '', doctor_address: '', doctor_phone: '' }));
  };

  const [form, setForm] = useState({
    // client_type is the payer on intake_submissions ('gapp' | 'medicaid' |
    // 'private_insurance' | 'self_pay'); service_type mirrors it for clients.service_type.
    client_type: '',
    service_type: '',
    first_name:'', middle_name:'', last_name:'', date_of_birth:'', gender:'',
    language:'', address:'', city:'', county:'', state:'GA', zip:'',
    medicaid_id:'', private_insurance:'',
    guardian_first_name:'', guardian_last_name:'', guardian_relationship:'', guardian_phone:'', guardian_email:'', guardian_address:'',
    emergency_first_name:'', emergency_last_name:'', emergency_relationship:'', emergency_phone:'', emergency_email:'', emergency_address:'',
    diagnosis_summary:'',
    doctor_name:'', doctor_practice:'', doctor_address:'', doctor_phone:'', physician_id: null,
    referral_source:'', consent_texts_emails: false, esignature:'',
    // Service lines — empty until the family answers step 1.
    service_lines: [],
    // DME fields
    dme_product_categories: [],
    dme_qualifying_diagnosis: '',
    dme_prescribing_physician: '',
    dme_physician_npi: '',
    dme_delivery_address_same: true,
    dme_delivery_address: '',
  });

  const toggleServiceLine = (line) => {
    setForm(f => {
      const service_lines = f.service_lines.includes(line)
        ? f.service_lines.filter(l => l !== line)
        : [...f.service_lines, line];
      // Dropping nursing withdraws GAPP as an option, so clear a stale choice
      // rather than submitting a payer the form no longer offers.
      const dropGapp = f.client_type === 'gapp' && !service_lines.includes('home_health');
      return dropGapp
        ? { ...f, service_lines, client_type: '', service_type: '' }
        : { ...f, service_lines };
    });
  };

  const toggleDmeCat = (cat) => {
    setForm(f => ({
      ...f,
      dme_product_categories: f.dme_product_categories.includes(cat)
        ? f.dme_product_categories.filter(c => c !== cat)
        : [...f.dme_product_categories, cat],
    }));
  };

  const hasDME = form.service_lines.includes('dme');
  const hasNursing = form.service_lines.includes('home_health');
  const isGAPP = form.client_type === 'gapp';
  // GAPP authorizes nursing, so it is not a payer for a supplies-only request.
  const payerOptions = [
    ...(hasNursing ? [{ key: 'gapp', label: t.gappClient, desc: t.gappDesc }] : []),
    { key: 'medicaid',          label: t.medicaidClient,  desc: t.medicaidDesc },
    { key: 'private_insurance', label: t.insuranceClient, desc: t.insuranceDesc },
    { key: 'self_pay',          label: t.selfPayClient,   desc: t.selfPayDesc },
  ];
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [emergencySameAsGuardian, setEmergencySameAsGuardian] = useState(false);
  // Household pets, so the right nurse is placed with the family.
  const [pets, setPets] = useState({ pets: [], other: '', notes: '' });
  const [petsError, setPetsError] = useState(null);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // County from ZIP — filled in for the family, never forced. A county they
  // pick by hand is never replaced; one CAERA filled may be, if they change
  // the ZIP. A ZIP that crosses county lines is offered as a short list, not
  // guessed. (No approved-area note here: whether to take a family outside it
  // is the office's call, made on the staff side.)
  const countyAuto = useRef(false);
  // Decided from the form as rendered; the update is a plain merge (a ref
  // mutated inside an updater breaks under React's double-run in development).
  const setZip = (e) => {
    const zip = e.target.value;
    const z = countyForZip(zip);
    const mayReplace = !form.county || countyAuto.current;
    const patch = { zip };
    if (mayReplace && z.kind === 'fill') { patch.county = z.counties[0]; countyAuto.current = true; }
    else if (mayReplace && z.kind === 'choose' && !z.counties.includes(form.county)) { patch.county = ''; countyAuto.current = false; }
    setForm(prev => ({ ...prev, ...patch }));
  };
  const setCounty = (e) => { countyAuto.current = false; setForm(prev => ({ ...prev, county: e.target.value })); };

  // While "Same as Parent / Guardian" is checked, keep Emergency Contact mirrored
  // to whatever the guardian/responsible-party fields currently say.
  useEffect(() => {
    if (!emergencySameAsGuardian) return;
    setForm(prev => ({
      ...prev,
      emergency_first_name: prev.guardian_first_name,
      emergency_last_name: prev.guardian_last_name,
      emergency_relationship: prev.guardian_relationship,
      emergency_phone: prev.guardian_phone,
      emergency_email: prev.guardian_email,
      emergency_address: prev.guardian_address,
    }));
  }, [emergencySameAsGuardian, form.guardian_first_name, form.guardian_last_name, form.guardian_relationship, form.guardian_phone, form.guardian_email, form.guardian_address]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) { setSubmitted(true); return; }
    if (!form.client_type || !form.first_name || !form.last_name
      || !form.guardian_first_name || !form.guardian_last_name || !form.guardian_phone
      || !form.emergency_first_name || !form.emergency_last_name || !form.emergency_phone
      || !form.esignature) {
      setError(t.errorRequired); return;
    }
    if (form.guardian_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.guardian_email)) {
      setError(t.errorEmail); return;
    }
    const petErr = petAnswerError(pets.pets, pets.other);
    setPetsError(petErr);
    if (petErr) { setError(PET_COPY[lang][petErr]); return; }
    setLoading(true); setError('');
    const sanitised = {
      ...form,
      first_name: sanitise(form.first_name),
      last_name: sanitise(form.last_name),
      address: sanitise(form.address),
      city: sanitise(form.city),
      guardian_first_name: sanitise(form.guardian_first_name),
      guardian_last_name: sanitise(form.guardian_last_name),
      guardian_address: sanitise(form.guardian_address),
      emergency_first_name: sanitise(form.emergency_first_name),
      emergency_last_name: sanitise(form.emergency_last_name),
      emergency_address: sanitise(form.emergency_address),
      diagnosis_summary: sanitise(form.diagnosis_summary),
      physician_id: form.physician_id || null,
      doctor_name: sanitise(form.doctor_name),
      doctor_practice: sanitise(form.doctor_practice),
      private_insurance: form.private_insurance === 'Yes',
      form_language: lang,
      ...petColumns(pets.pets, sanitise(pets.other), sanitise(pets.notes)),
    };
    const { error: err, data } = await submitIntakeForm(sanitised);
    if (err) { 
      console.error('Intake submission error:', err);
      console.error('Error details:', err.message, err.details, err.hint);
      setError(t.errorGeneral); 
      setLoading(false); 
      return; 
    }
    // Guardian-facing "Next Steps" email.
    try {
      await notifyNewIntake(form, form.client_type);
    } catch (e) { console.error('Intake notification error:', e); }
    // The office hears about it in the team Space from the database trigger
    // (notify_office), not from this page.
    setSubmitted(true);
  };

  if (submitted) return (
    <div style={{ minHeight:'100vh', background:'var(--accent-bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:20, fontFamily:'DM Sans, sans-serif' }}>
      <div style={{ background:'#fff', borderRadius:16, padding:48, maxWidth:480, width:'100%', textAlign:'center', border:'1px solid var(--border)' }}>
        <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--success-bg)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
          <svg width="32" height="32" fill="none" stroke="var(--success)" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <h2 style={{ color:'var(--text)', fontSize:22, fontWeight:600, marginBottom:12 }}>{t.successTitle}</h2>
        <p style={{ color:'var(--text2)', fontSize:14, lineHeight:1.6, marginBottom:8 }}>{t.successMsg}</p>
        <p style={{ color:'var(--text3)', fontSize:12 }}>{t.successSub}</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'var(--accent-bg)', fontFamily:'DM Sans, sans-serif', padding:'24px 16px' }}>
      <div style={{ maxWidth:680, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <img src="/logo.jpg" alt="A Heart For Care" style={{ width:80, height:80, borderRadius:12, objectFit:'cover', marginBottom:12, boxShadow:'0 4px 16px rgba(13,43,78,0.15)' }} />
          <h1 style={{ color:'var(--text)', fontSize:22, fontWeight:600, marginBottom:4 }}>{t.title}</h1>
          <p style={{ color:'var(--text3)', fontSize:13 }}>{t.subtitle}</p>

          {/* Language toggle */}
          <div style={{ display:'inline-flex', background:'var(--border)', borderRadius:8, padding:3, marginTop:14 }}>
            {['en','es'].map(l => (
              <button key={l} onClick={() => setLang(l)} style={{
                padding:'6px 18px', borderRadius:6, border:'none', cursor:'pointer',
                fontSize:13, fontWeight:500, fontFamily:'inherit',
                background: lang === l ? 'var(--navy)' : 'transparent',
                color: lang === l ? '#fff' : 'var(--text2)',
                transition:'all 0.15s',
              }}>
                {l === 'en' ? '🇺🇸 English' : '🇲🇽 Español'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background:'#fff', borderRadius:16, border:'1px solid var(--border)', padding:'28px 32px' }}>
          {/* Honeypot */}
          <div style={{ display:'none' }} aria-hidden="true">
            <input tabIndex="-1" autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
          </div>

          <form onSubmit={handleSubmit}>
            {/* ── STEP 1: WHAT DO YOU NEED (service lines) ── */}
            {/* Families know what they need long before they know which
                programme pays for it, so the service question comes first and
                the payer options below are narrowed by the answer. */}
            <Section title={t.selectClientType}>
              <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14 }}>{t.clientTypeDesc}</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                  { key:'home_health', label:t.homeHealthLabel, desc:t.homeHealthDesc },
                  { key:'dme',         label:t.dmeLabel,        desc:t.dmeDesc },
                ].map(({ key, label, desc }) => {
                  const active = form.service_lines.includes(key);
                  return (
                    <label key={key} style={{
                      flex:'1 1 220px', display:'flex', gap:12, alignItems:'flex-start', cursor:'pointer',
                      padding:'14px 16px', borderRadius:10, border:`2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                      background:'var(--accent-bg)', transition:'all 0.15s',
                    }}>
                      <input type="checkbox" checked={active} onChange={() => toggleServiceLine(key)}
                        style={{ width:18, height:18, marginTop:2, flexShrink:0, accentColor:'var(--accent)', cursor:'pointer' }} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{label}</div>
                        <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </Section>

            {/* ── STEP 2: WHO PAYS ── */}
            {form.service_lines.length > 0 && (
            <Section title={t.selectPayer}>
              <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14 }}>{t.payerDesc}</div>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                {payerOptions.map(({ key, label, desc }) => {
                  const active = form.client_type === key;
                  return (
                    <label key={key} style={{
                      flex:'1 1 200px', display:'flex', gap:12, alignItems:'flex-start', cursor:'pointer',
                      padding:'14px 16px', borderRadius:10, border:`2px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                      background: active ? 'var(--accent-bg)' : 'var(--accent-bg)', transition:'all 0.15s',
                    }}>
                      <input type="radio" name="client_type" value={key} checked={active}
                        onChange={e => setForm(p => ({ ...p, client_type: e.target.value, service_type: e.target.value }))}
                        style={{ width:18, height:18, marginTop:2, flexShrink:0, accentColor:'var(--accent)', cursor:'pointer' }} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{label}</div>
                        <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </Section>
            )}

            {form.client_type && (
              <>
                {/* ── CLIENT/PATIENT INFORMATION ── */}
                <Section title={t.clientInfo}>
                  <div className="form-row">
                    <Field label={t.firstName} required><input className="form-input" value={form.first_name} onChange={set('first_name')} required maxLength={50} /></Field>
                    <Field label={t.lastName} required><input className="form-input" value={form.last_name} onChange={set('last_name')} required maxLength={50} /></Field>
                  </div>
                  <div className="form-row">
                    <Field label={t.dob}><input type="date" className="form-input" value={form.date_of_birth} onChange={set('date_of_birth')} /></Field>
                    <Field label={t.gender}>
                      <select className="form-select" value={form.gender} onChange={set('gender')}>
                        <option value="">{t.selectGender}</option>
                        <option value="Male">{t.male}</option><option value="Female">{t.female}</option><option value="Other">{t.other}</option>
                      </select>
                    </Field>
                  </div>
                  <div className="form-row">
                    <Field label={t.language}><input className="form-input" value={form.language} onChange={set('language')} maxLength={50} /></Field>
                    <Field label={t.zip}><input className="form-input" value={form.zip} onChange={setZip} maxLength={10} inputMode="numeric" /></Field>
                  </div>
                  <Field label={t.address}><input className="form-input" value={form.address} onChange={set('address')} maxLength={100} /></Field>
                  <div className="form-row">
                    <Field label={t.city}><input className="form-input" value={form.city} onChange={set('city')} maxLength={50} /></Field>
                    <Field label={t.county}>
                    {(() => {
                      const z = countyForZip(form.zip);
                      const fromZip = z.kind !== 'unknown' ? z.counties : [];
                      const note = !form.county && z.kind === 'choose'
                        ? t.countyChoose.replace('{list}', z.counties.join(', '))
                        : (form.county && countyAuto.current ? t.countyFromZip : null);
                      return (
                        <>
                          <select className="form-select" value={form.county} onChange={setCounty}>
                            <option value="">{t.selectCounty}</option>
                            {fromZip.map(c => <option key={`z-${c}`} value={c}>{c}</option>)}
                            {fromZip.length > 0 && <option disabled>──────────</option>}
                            {GA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          {note && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{note}</div>}
                        </>
                      );
                    })()}
                  </Field>
                  </div>
                  <div className="form-row">
                    <Field label={t.medicaidId}><input className="form-input" value={form.medicaid_id} onChange={set('medicaid_id')} maxLength={20} /></Field>
                    <Field label={t.privateInsurance}>
                      <select className="form-select" value={form.private_insurance} onChange={set('private_insurance')}>
                        <option value="">—</option>
                        <option value="Yes">{t.yes}</option>
                        <option value="No">{t.no}</option>
                      </select>
                    </Field>
                  </div>
                  {form.private_insurance === 'Yes' && (
                    <Field label={t.insuranceName}><input className="form-input" value={form.insurance_name} onChange={set('insurance_name')} maxLength={100} /></Field>
                  )}
                </Section>

                {/* ── GUARDIAN / RESPONSIBLE PARTY INFORMATION ── */}
                <Section title={isGAPP ? t.guardianInfo : t.responsible}>
                  <div className="form-row">
                    <Field label={t.firstName} required><input className="form-input" value={form.guardian_first_name} onChange={set('guardian_first_name')} required maxLength={50} /></Field>
                    <Field label={t.lastName} required><input className="form-input" value={form.guardian_last_name} onChange={set('guardian_last_name')} required maxLength={50} /></Field>
                  </div>
                  <Field label={t.guardianRelationship}><input className="form-input" value={form.guardian_relationship} onChange={set('guardian_relationship')} maxLength={50} /></Field>
                  <div className="form-row">
                    <Field label={t.guardianPhone} required><input type="tel" className="form-input" value={form.guardian_phone} onChange={set('guardian_phone')} required maxLength={15} /></Field>
                    <Field label={t.guardianEmail}><input type="email" className="form-input" value={form.guardian_email} onChange={set('guardian_email')} maxLength={100} /></Field>
                  </div>
                  <Field label={t.contactAddressLabel}>
                    <div style={{ display:'flex', gap:8 }}>
                      <input className="form-input" value={form.guardian_address} onChange={set('guardian_address')} maxLength={200} />
                      <button type="button" onClick={() => setForm(p => ({ ...p, guardian_address: [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ') }))}
                        style={{ background:'var(--accent-bg)', color:'var(--accent)', border:'1px solid var(--accent-border)', borderRadius:7, padding:'0 12px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                        {t.quickFillChildAddress}
                      </button>
                    </div>
                  </Field>
                </Section>

                {/* ── EMERGENCY CONTACT ── */}
                <Section title={t.emergencyInfo}>
                  <label style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 13px', marginBottom:16, background:'var(--copper-bg)', border:'1px solid var(--copper-border)', borderRadius:7, cursor:'pointer' }}>
                    <input type="checkbox" checked={emergencySameAsGuardian} onChange={e => setEmergencySameAsGuardian(e.target.checked)} style={{ width:16, height:16, accentColor:'var(--copper)', cursor:'pointer', flexShrink:0 }} />
                    <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>
                      {isGAPP ? t.sameAsGuardianLabel : t.sameAsResponsibleLabel} <span style={{ fontSize:11.5, color:'var(--text3)', fontWeight:400, marginLeft:4 }}>{t.sameAsCopySub}</span>
                    </span>
                  </label>
                  <div className="form-row">
                    <Field label={t.firstName} required><input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_first_name} onChange={set('emergency_first_name')} required maxLength={50} /></Field>
                    <Field label={t.lastName} required><input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_last_name} onChange={set('emergency_last_name')} required maxLength={50} /></Field>
                  </div>
                  <Field label={t.emergencyRelationship}><input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_relationship} onChange={set('emergency_relationship')} maxLength={50} /></Field>
                  <div className="form-row">
                    <Field label={t.emergencyPhone} required><input type="tel" className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_phone} onChange={set('emergency_phone')} required maxLength={15} /></Field>
                    <Field label={t.guardianEmail}><input type="email" className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_email} onChange={set('emergency_email')} maxLength={100} /></Field>
                  </div>
                  <Field label={t.contactAddressLabel}>
                    <div style={{ display:'flex', gap:8 }}>
                      <input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_address} onChange={set('emergency_address')} maxLength={200} />
                      <button type="button" disabled={emergencySameAsGuardian} onClick={() => setForm(p => ({ ...p, emergency_address: p.guardian_address }))}
                        style={{ background:'var(--accent-bg)', color:'var(--accent)', border:'1px solid var(--accent-border)', borderRadius:7, padding:'0 12px', fontSize:12, fontWeight:600, cursor: emergencySameAsGuardian ? 'not-allowed' : 'pointer', whiteSpace:'nowrap', opacity: emergencySameAsGuardian ? 0.5 : 1 }}>
                        {t.quickFillAboveAddress}
                      </button>
                    </div>
                  </Field>
                </Section>

                {/* ── DME SECTION (conditional) ── */}
                {hasDME && (
                  <Section title={t.dmeDetailsTitle}>
                    <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
                      {t.dmeDetailsDesc}
                    </div>
                    <Field label={t.productCategoriesLabel}>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                        {[
                          // Product category is a parallel identifier, not a status — neutral for all.
                          { key:'incontinence', label:t.incontinenceLabel, color:'var(--text3)', bg:'var(--bg2)' },
                          { key:'ostomy',       label:t.ostomyLabel,       color:'var(--text3)', bg:'var(--bg2)' },
                          { key:'catheter',     label:t.catheterLabel,     color:'var(--text3)', bg:'var(--bg2)' },
                        ].map(({ key, label, color, bg }) => {
                          const active = form.dme_product_categories.includes(key);
                          return (
                            <button key={key} type="button" onClick={() => toggleDmeCat(key)} style={{
                              padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:500, cursor:'pointer', fontFamily:'inherit',
                              background: active ? bg : 'var(--accent-bg)',
                              color: active ? color : 'var(--text3)',
                              border: `1.5px solid ${active ? color : 'var(--border)'}`,
                              transition:'all 0.15s',
                            }}>
                              {active ? '✓ ' : ''}{label}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                    <Field label={t.qualifyingDiagnosisLabel}>
                      <select className="form-select" value={form.dme_qualifying_diagnosis} onChange={set('dme_qualifying_diagnosis')}>
                        <option value="">{t.selectPlaceholder}</option>
                        {t.dmeDiagnoses.map(d => <option key={d}>{d}</option>)}
                      </select>
                    </Field>
                    <div className="form-row">
                      <Field label={t.prescribingPhysicianLabel}>
                        <input className="form-input" value={form.dme_prescribing_physician} onChange={set('dme_prescribing_physician')} placeholder="Dr. Smith" maxLength={100} />
                      </Field>
                      <Field label={t.physicianNpiLabel}>
                        <input className="form-input" value={form.dme_physician_npi} onChange={set('dme_physician_npi')} placeholder="1234567890" maxLength={10} />
                      </Field>
                    </div>
                    <Field label={t.deliveryAddressLabel}>
                      <label style={{ display:'flex', gap:8, alignItems:'center', fontSize:12, color:'var(--text2)', marginBottom:8, cursor:'pointer' }}>
                        <input type="checkbox" checked={form.dme_delivery_address_same} onChange={e => setForm(f => ({ ...f, dme_delivery_address_same: e.target.checked }))}
                          style={{ accentColor:'var(--accent)' }} />
                        {t.sameAsHomeAddress}
                      </label>
                      {!form.dme_delivery_address_same && (
                        <input className="form-input" value={form.dme_delivery_address} onChange={set('dme_delivery_address')} placeholder={t.deliveryAddressPlaceholder} maxLength={200} />
                      )}
                    </Field>
                  </Section>
                )}

                {/* ── MEDICAL INFORMATION ── */}
                <Section title={t.medicalInfo}>
                  <Field label={t.diagnosisSummary} required>
                    <textarea className="form-textarea" value={form.diagnosis_summary} onChange={set('diagnosis_summary')} rows={3} maxLength={1000} />
                  </Field>
                  <Field label={t.doctorSearch}>
                    <div>
                      <PhysicianPicker physicians={publicPhysicians} value={pickedDoctor} onChange={pickDoctor}
                        placeholder={t.doctorSearchPlaceholder} addLabel={t.doctorNotListed} noMatchLabel={t.doctorNoMatch}
                        onAddNew={() => { setPickedDoctor(null); setTypeDoctor(true); setForm(f => ({ ...f, physician_id: null })); }} />
                      {!pickedDoctor && !typeDoctor && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{t.doctorSearchHint}</div>}
                    </div>
                  </Field>
                  {!pickedDoctor && (typeDoctor || form.doctor_name) && (
                    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', marginTop: 4 }}>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>{t.doctorTyped}</div>
                      <div className="form-row">
                        <Field label={t.doctorName}><input className="form-input" value={form.doctor_name} onChange={set('doctor_name')} maxLength={100} /></Field>
                        <Field label={t.doctorPractice}><input className="form-input" value={form.doctor_practice} onChange={set('doctor_practice')} maxLength={100} /></Field>
                      </div>
                      <div className="form-row">
                        <Field label={t.doctorAddress}><input className="form-input" value={form.doctor_address} onChange={set('doctor_address')} maxLength={200} /></Field>
                        <Field label={t.doctorPhone}><input type="tel" className="form-input" value={form.doctor_phone} onChange={set('doctor_phone')} maxLength={15} /></Field>
                      </div>
                    </div>
                  )}
                </Section>

                {/* ── HOUSEHOLD ── */}
                <Section title={PET_COPY[lang].section}>
                  <PetsQuestion lang={lang} value={pets} error={petsError}
                    onChange={v => { setPets(v); if (petsError) setPetsError(petAnswerError(v.pets, v.other)); }} />
                </Section>

                {/* ── REFERRAL SOURCE ── */}
                <Section title={t.referralSource}>
                  <Field label={t.referralSource}>
                    <select className="form-select" value={form.referral_source} onChange={set('referral_source')}>
                      <option value="">{t.selectSource}</option>
                      {t.sources.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                </Section>

                {/* ── CONSENT ── */}
                <div style={{ background:'var(--accent-bg)', borderRadius:10, padding:16, marginBottom:20 }}>
                  <label style={{ display:'flex', gap:12, cursor:'pointer', fontSize:13, color:'var(--text2)' }}>
                    <input type="checkbox" checked={form.consent_texts_emails} onChange={set('consent_texts_emails')} style={{ width:18, height:18, marginTop:1, flexShrink:0, accentColor:'var(--accent)' }} />
                    {t.consentLabel}
                  </label>
                </div>

                {/* ── SIGNATURE ── */}
                <Field label={t.signatureLabel} required>
                  <input className="form-input" value={form.esignature} onChange={set('esignature')} placeholder={t.signaturePlaceholder} required maxLength={100} />
                </Field>

                {error && (
                  <div style={{ background:'var(--danger-bg)', border:'1px solid var(--danger-bg)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'var(--danger)' }}>{error}</div>
                )}

                <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading} style={{ marginTop:8 }}>
                  {loading ? <span className="loading-spinner" style={{ borderTopColor:'#fff' }} /> : null}
                  {loading ? t.submitting : t.submit}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
